import {
  Stack,
  TextField,
  useTheme,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Dialog,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  DialogTitle,
  DialogActions,
  Snackbar,
  Alert,
  LinearProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Box,
  Divider,
  Collapse,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Tooltip,
  Fade,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  CardHeader,
  InputAdornment,
  MenuItem,
  Select,
  DialogContent,
  Zoom,
  Popover,
  Switch,
  FormGroup,
} from '@mui/material';
import Slide from '@mui/material/Slide';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import ReactHtmlParser from 'react-html-parser';
import 'react-quill/dist/quill.snow.css';
import useMediaQuery from '@mui/material/useMediaQuery';
import styled from '@emotion/styled';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import BusinessIcon from '@mui/icons-material/Business';
import imagepreview from '../../media/bannerup.png';
import NoimageFound from '../../media/noimage.jpg';
import axios from '../../axios';
import './animation.css';

const useStyles = styled((theme) => ({
  fileInput: {
    display: 'none',
  },
}));

// Header styled component - matching all masters
const HeaderPaper = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
  borderRadius: '12px',
  padding: '16px 24px',
  marginBottom: '16px',
  boxShadow: '0 4px 20px rgba(19, 78, 94, 0.3)',
}));

// Case Study Card - proper informative card design
const CaseStudyCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  border: '1px solid #e8ecf0',
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
    '& .case-overlay': {
      opacity: 1,
    },
  },
}));

const CaseOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  background: 'rgba(0,0,0,0.5)',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  backdropFilter: 'blur(4px)',
  zIndex: 5,
  pointerEvents: 'none',
  '& .MuiIconButton-root': {
    pointerEvents: 'auto',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: '#fff',
  fontWeight: 700,
  textAlign: 'left',
  backgroundColor: '#1a3a4a',
  padding: '8px 12px',
  whiteSpace: 'nowrap',
  fontSize: '0.75rem',
  letterSpacing: '0.03em',
  borderBottom: '2px solid rgba(255,255,255,0.1)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
    transition: 'background-color 0.2s ease',
  },
}));

const StickyTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: '70vh',
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
}));

// Quill modules configuration to prevent empty paragraphs
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image', 'video'],
    [{ align: [] }],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
};

// Helper function to clean HTML content
const cleanHtmlContent = (html) => {
  if (!html) return '';
  // Remove empty paragraphs at the beginning
  let cleaned = html.replace(/^(<p><br\s*\/?><\/p>)+/, '');
  // Remove empty paragraphs at the end
  cleaned = cleaned.replace(/(<p><br\s*\/?><\/p>)+$/, '');
  // Remove empty paragraphs that only contain &nbsp; or whitespace
  cleaned = cleaned.replace(/<p>\s*(&nbsp;|\s)*\s*<\/p>/g, '');
  return cleaned;
};

// Fullscreen Image Preview Dialog with transparent background
const FullscreenImageDialog = ({ open, imageUrl, onClose, title }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.92)',
          backdropFilter: 'blur(8px)',
          boxShadow: 'none',
          borderRadius: 0,
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          p: 4,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.25)',
            },
            zIndex: 10,
          }}
        >
          <CloseIcon sx={{ fontSize: 32 }} />
        </IconButton>

        {title && (
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              position: 'absolute',
              top: 20,
              left: 50,
              fontWeight: 600,
              zIndex: 10,
            }}
          >
            {title}
          </Typography>
        )}

        <Zoom in={open} timeout={400}>
          <Box
            component="img"
            src={imageUrl || NoimageFound}
            alt="Preview"
            sx={{
              maxWidth: '95%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          />
        </Zoom>

        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.4)',
            mt: 3,
            letterSpacing: '0.1em',
          }}
        >
          Click anywhere outside the image or press ESC to close
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

const CaseStudy = () => {
  const tokent =
    'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJldmVudGxpc3QiOlt7IlVzZXJJRCI6IjEiLCJMb2dpbkNvZGUiOiIwMSIsIkxvZ2luTmFtZSI6IkFkbWluIiwiRW1haWxJZCI6ImFkbWluQGdtYWlsLmNvbSIsIlVzZXJUeXBlIjoiQURNSU4ifV0sImlhdCI6MTYzODM1NDczMX0.ZW6zEHIXTxfT-QWEzS6-GuY7bRupf2Jc_tp4fXIRabQ';
  const theme = useTheme();
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [openAlert, setopenAlert] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [operation, setOperation] = useState('Add');
  const [alertType, setAlertType] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [openLoader, setOpenLoader] = useState(false);
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const editorWrapperRef = useRef(null);
  const [viewMode, setViewMode] = useState('grid');

  // Filter states using Popover
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [reviewFilter, setReviewFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');

  const [reviewData, setReviewData] = useState([]);
  const [CaseStudyTitle, setCaseStudyTitle] = useState('');
  const [Category, setCategory] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState('');
  const [CaseStudyDescription, setCaseStudyDescription] = useState('');
  const [Summary, setSummary] = useState('');
  const [ActiveStatus, setActiveStatus] = useState('Y');
  const [Review, setReview] = useState(null);

  const [imagePreview, setImagePreview] = useState('');
  const [existingImageName, setExistingImageName] = useState('');
  const [CaseStudyData, setCaseStudyData] = useState([]);
  const [IdToDelete, setIdToDelete] = useState(null);
  const [editID, setEditID] = useState();

  // Fullscreen image preview state
  const [previewImageOpen, setPreviewImageOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImageTitle, setPreviewImageTitle] = useState('');

  const [errors, setErrors] = useState({
    CaseStudyTitle: false,
    Category: false,
    selectedImageFile: false,
    CaseStudyDescription: false,
    Review: false,
    Summary: false,
    ActiveStatus: false,
  });

  const [helperTexts, setHelperTexts] = useState({
    CaseStudyTitle: '',
    Category: '',
    selectedImageFile: '',
    CaseStudyDescription: '',
    Review: '',
    Summary: '',
    ActiveStatus: '',
  });

  // Filter options for Autocomplete
  const statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'Y' },
    { label: 'Inactive', value: 'N' },
  ];

  const reviewOptions = [
    { label: 'All Reviews', value: 'all' },
    ...(() => {
      const reviews = CaseStudyData.filter((item) => item.CompanyName).map((item) => ({
        label: item.CompanyName,
        value: item.CompanyName,
      }));
      return [...new Map(reviews.map((item) => [item.value, item])).values()];
    })(),
  ];

  useEffect(() => {
    getAllCaseStudy();
    getAllReview();
  }, []);

  const getAllCaseStudy = async () => {
    setOpenLoader(true);
    try {
      const res = await axios.instance.get('/getAllCaseStudy', {
        headers: { Authorization: tokent, 'Content-Type': 'application/json' },
      });
      setCaseStudyData(res.data);
      setOpenLoader(false);
    } catch (error) {
      console.error('Error fetching case studies:', error);
      setOpenLoader(false);
    }
  };

  const getAllReview = async () => {
    setOpenLoader(true);
    try {
      const res = await axios.instance.get('/getAllReviewData');
      setReviewData(res.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
    setOpenLoader(false);
  };

  const InsertCaseStudy = async (newUser) => {
    try {
      await axios.instance.post('/InsertCaseStudy', newUser, {
        headers: {
          Authorization: tokent,
          'Content-Type': 'multipart/form-data',
        },
      });
      getAllCaseStudy();
      setAlertType('success');
      setAlertMessage('New Case Study Added Successfully!');
      setopenAlert(true);
    } catch (error) {
      console.error('Error adding Case Study:', error);
      setAlertType('error');
      setAlertMessage('Failed to add the Case Study.');
      setopenAlert(true);
    }
  };

  const UpdateCaseStudy = async (userId, updatedUser) => {
    try {
      await axios.instance.put(`/UpdateCaseStudy/${userId}`, updatedUser, {
        headers: {
          Authorization: tokent,
          'Content-Type': 'multipart/form-data',
        },
      });
      setopenAlert(true);
      setEditID();
      getAllCaseStudy();
      setAlertType('success');
      setAlertMessage('Case Study Updated Successfully!');
    } catch (error) {
      console.error('Error updating Case Study:', error);
      setAlertType('error');
      setAlertMessage('Failed to update the Case Study.');
      setopenAlert(true);
    }
  };

  const DeleteCaseStudy = async (userId) => {
    try {
      await axios.instance
        .delete(`/DeleteCaseStudy/${userId}`, {
          headers: { Authorization: tokent, 'Content-Type': 'application/json' },
        })
        .then((res) => {
          if (res.data === '') {
            getAllCaseStudy();
            setOpenDelete(false);
            setAlertType('warning');
            setAlertMessage('Case Study Deleted Successfully!');
            setopenAlert(true);
          } else {
            getAllCaseStudy();
            setOpenDelete(false);
            setAlertType('error');
            setAlertMessage("Oops! Can't delete this data. It's connected to other information.");
            setopenAlert(true);
          }
        });
    } catch (error) {
      console.error('Error deleting Case Study:', error);
      setAlertType('error');
      setAlertMessage('Failed to delete the Case Study.');
      setopenAlert(true);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setErrors((prev) => ({ ...prev, selectedImageFile: false }));
  };

  const resetFields = () => {
    setCaseStudyTitle('');
    setCategory('');
    setSelectedImageFile('');
    setExistingImageName('');
    setCaseStudyDescription('');
    setSummary('');
    setActiveStatus('Y');
    setReview(null);
    setImagePreview('');
    setErrors({
      CaseStudyTitle: false,
      Category: false,
      selectedImageFile: false,
      CaseStudyDescription: false,
      Review: false,
      Summary: false,
      ActiveStatus: false,
    });
    setHelperTexts({
      CaseStudyTitle: '',
      Category: '',
      selectedImageFile: '',
      CaseStudyDescription: '',
      Review: '',
      Summary: '',
      ActiveStatus: '',
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Clean the description before validation
    const cleanedDescription = cleanHtmlContent(CaseStudyDescription);
    setCaseStudyDescription(cleanedDescription);

    const inputValidation =
      !CaseStudyTitle || !Category || !selectedImageFile || !cleanedDescription || !Summary || !ActiveStatus;

    if (inputValidation) {
      if (!selectedImageFile) {
        setErrors((prev) => ({ ...prev, selectedImageFile: true }));
        setHelperTexts((prev) => ({ ...prev, selectedImageFile: 'Please upload image' }));
      }
      if (!CaseStudyTitle) {
        setErrors((prev) => ({ ...prev, CaseStudyTitle: true }));
        setHelperTexts((prev) => ({ ...prev, CaseStudyTitle: 'Please enter title' }));
      }
      if (!Category) {
        setErrors((prev) => ({ ...prev, Category: true }));
        setHelperTexts((prev) => ({ ...prev, Category: 'Please enter category' }));
      }
      if (!cleanedDescription) {
        setErrors((prev) => ({ ...prev, CaseStudyDescription: true }));
        setHelperTexts((prev) => ({ ...prev, CaseStudyDescription: 'Please enter description' }));
      }
      if (!Summary) {
        setErrors((prev) => ({ ...prev, Summary: true }));
        setHelperTexts((prev) => ({ ...prev, Summary: 'Please enter summary' }));
      }
      if (!ActiveStatus) {
        setErrors((prev) => ({ ...prev, ActiveStatus: true }));
        setHelperTexts((prev) => ({ ...prev, ActiveStatus: 'Please select status' }));
      }
      if (Review && editID !== Review.CaseStudyId) {
        const existingReviewIds = CaseStudyData.filter((item) => item.ActiveStatus === 'Y').map(
          (item) => item.ReviewId
        );
        if (existingReviewIds.includes(Review.ReviewId)) {
          setErrors((prev) => ({ ...prev, Review: true }));
          setHelperTexts((prev) => ({ ...prev, Review: 'Case Study already exists for this Review' }));
        }
      }
      return;
    }

    const formData = new FormData();
    const imageValue =
      selectedImageFile instanceof File
        ? selectedImageFile
        : operation === 'Edit'
        ? existingImageName || selectedImageFile || ''
        : selectedImageFile || '';

    formData.append('CaseStudyTitle', CaseStudyTitle);
    formData.append('Category', Category);
    formData.append('CaseStudyFileName', imageValue);
    formData.append('CaseStudyDescription', cleanedDescription);
    formData.append('Summary', Summary);
    formData.append('ReviewId', Review ? Review.ReviewId : 0);
    formData.append('ActiveStatus', ActiveStatus);
    formData.append(operation === 'Edit' ? 'ModifyedBy' : 'CreatedBy', 5);

    if (operation === 'Add') {
      InsertCaseStudy(formData);
    } else {
      UpdateCaseStudy(editID, formData);
    }
    resetFields();
    handleClose();
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'CaseStudyTitle') setCaseStudyTitle(value);
    if (name === 'Category') setCategory(value);
    if (name === 'Summary') setSummary(value);
    if (name === 'ActiveStatus') setActiveStatus(value);
    validateInput(name, value);
  };

  const handleReviewChange = (value) => {
    setReview(value);
    validateInput('Review', value);
  };

  const handleChange2 = (value) => {
    const cleanedValue = cleanHtmlContent(value);
    setCaseStudyDescription(cleanedValue);
  };

  const toggleEditorFullscreen = () => {
    const wrapper = editorWrapperRef.current;
    if (!wrapper) return;
    const nextState = !isEditorFullscreen;
    wrapper.classList.toggle('fullscreen-editor', nextState);
    setIsEditorFullscreen(nextState);
    document.body.style.overflow = nextState ? 'hidden' : '';
  };

  const validateInput = (name, value) => {
    let error = false;
    let helperText = '';

    if (name === 'CaseStudyTitle') {
      if (!value.trim()) {
        error = true;
        helperText = 'Field cannot be empty';
      } else if (value.length > 100) {
        error = true;
        helperText = 'Cannot exceed 100 characters';
      }
    } else if (name === 'Category') {
      if (!value.trim()) {
        error = true;
        helperText = 'Field cannot be empty';
      } else if (value.length > 50) {
        error = true;
        helperText = 'Cannot exceed 50 characters';
      }
    } else if (name === 'Summary') {
      if (!value.trim()) {
        error = true;
        helperText = 'Field cannot be empty';
      }
    } else if (name === 'Review') {
      if (value) {
        const existingReviewIds = CaseStudyData.filter((item) => item.ActiveStatus === 'Y').map(
          (item) => item.ReviewId
        );
        if (editID !== value.CaseStudyId && existingReviewIds.includes(value.ReviewId)) {
          error = true;
          helperText = 'Case Study already exists for this Review';
        }
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    setHelperTexts((prev) => ({ ...prev, [name]: helperText }));
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    if (value && value.length > 0) {
      validateInput(name, value);
    }
  };

  const handleAddOpen = () => {
    resetFields();
    setOpen(true);
    setOperation('Add');
  };

  const handleEdit = (user) => {
    resetFields();
    setErrors({
      CaseStudyTitle: false,
      Category: false,
      selectedImageFile: false,
      CaseStudyDescription: false,
      Review: false,
      Summary: false,
      ActiveStatus: false,
    });
    setHelperTexts({
      CaseStudyTitle: '',
      Category: '',
      selectedImageFile: '',
      CaseStudyDescription: '',
      Review: '',
      Summary: '',
      ActiveStatus: '',
    });
    if (user.ReviewId) {
      setReview({
        ReviewId: user.ReviewId,
        CompanyName: user.CompanyName,
        LogoFileName: user.LogoFileName,
        CaseStudyId: user.CaseStudyId,
      });
    } else {
      setReview(null);
    }
    setEditID(user.CaseStudyId);
    setCaseStudyTitle(user.CaseStudyTitle);
    setCategory(user.Category);
    const imageName = user.CaseStudyFileName || '';
    const imageUrl = imageName ? `${axios.baseURL}CaseStudy/${imageName}` : '';
    setExistingImageName(imageName);
    setSelectedImageFile(imageName);
    setImagePreview(imageUrl);
    // Clean the description when editing
    const cleanDescription = cleanHtmlContent(user.CaseStudyDescription || '');
    setCaseStudyDescription(cleanDescription);
    setSummary(user.Summary);
    setActiveStatus(user.ActiveStatus);
    setOpen(true);
    setOperation('Edit');
  };

  const handleDelete = (courseId) => {
    setOpenDelete(true);
    setIdToDelete(courseId);
  };

  const handleDeleteConfirmed = () => {
    DeleteCaseStudy(IdToDelete);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleCloseAlert = () => {
    setopenAlert(false);
  };

  const handleClose = () => {
    resetFields();
    setOpen(false);
    setIsEditorFullscreen(false);
    if (editorWrapperRef.current) {
      editorWrapperRef.current.classList.remove('fullscreen-editor');
    }
    document.body.style.overflow = '';
  };

  const handleImagePreview = (imageUrl, title) => {
    if (!imageUrl || imageUrl === NoimageFound) {
      return;
    }
    setPreviewImageUrl(imageUrl);
    setPreviewImageTitle(title || 'Case Study Image');
    setPreviewImageOpen(true);
  };

  const handleCloseImagePreview = () => {
    setPreviewImageOpen(false);
    setPreviewImageUrl('');
    setPreviewImageTitle('');
  };

  // Filter functions with Popover
  const handleFilterOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const getFilteredData = () => {
    let filtered = CaseStudyData;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        Object.values(item).some((value) => typeof value === 'string' && value.toLowerCase().includes(searchLower))
      );
    }

    if (activeFilter && activeFilter.value !== 'all') {
      filtered = filtered.filter((item) => item.ActiveStatus === activeFilter.value);
    }

    if (reviewFilter && reviewFilter.value !== 'all') {
      filtered = filtered.filter((item) => {
        const companyName = item.CompanyName || '';
        return companyName.toLowerCase() === reviewFilter.value.toLowerCase();
      });
    }

    if (categoryFilter) {
      filtered = filtered.filter((item) => item.Category?.toLowerCase().includes(categoryFilter.toLowerCase()));
    }

    return filtered;
  };

  const filteredData = getFilteredData();

  const clearAllFilters = () => {
    setActiveFilter(null);
    setReviewFilter(null);
    setCategoryFilter('');
    setSearchTerm('');
    handleFilterClose();
  };

  const [openCollapse, setOpenCollapse] = useState({});
  const toggleCollapse = (id) => {
    setOpenCollapse((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (activeFilter && activeFilter.value !== 'all') count += 1;
    if (reviewFilter && reviewFilter.value !== 'all') count += 1;
    if (categoryFilter) count += 1;
    if (searchTerm) count += 1;
    return count;
  };

  // Render proper case study card view - NO PAGINATION
  const renderCardView = () => {
    return (
      <Grid container spacing={3}>
        {filteredData.map((user) => {
          const imageUrl = user.CaseStudyFileName
            ? `${axios.baseURL}CaseStudy/${user.CaseStudyFileName}`
            : NoimageFound;
          return (
            <Grid item xs={12} sm={6} md={4} key={user.CaseStudyId}>
              <Fade in timeout={400}>
                <CaseStudyCard>
                  {/* Case Study Image */}
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={imageUrl}
                      alt={user.CaseStudyTitle}
                      sx={{ objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => handleImagePreview(imageUrl, user.CaseStudyTitle)}
                    />
                    <CaseOverlay className="case-overlay">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(user);
                          }}
                          sx={{
                            color: '#fff',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(user.CaseStudyId);
                          }}
                          sx={{
                            color: '#fff',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Show Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCollapse(user.CaseStudyId);
                          }}
                          sx={{
                            color: '#fff',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                          }}
                        >
                          <ArrowDropDownCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CaseOverlay>

                    {/* Status Chips */}
                    <Chip
                      label={user.ActiveStatus === 'Y' ? 'Active' : 'Inactive'}
                      color={user.ActiveStatus === 'Y' ? 'success' : 'error'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        fontWeight: 600,
                        zIndex: 3,
                      }}
                    />
                    {user.CompanyName && (
                      <Chip
                        icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                        label={user.CompanyName}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 12,
                          left: 12,
                          fontWeight: 500,
                          zIndex: 3,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: '#fff',
                        }}
                      />
                    )}
                  </Box>

                  {/* Case Study Content */}
                  <CardHeader
                    title={
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.3 }}>
                        {user.CaseStudyTitle}
                      </Typography>
                    }
                    subheader={
                      <Typography variant="caption" color="text.secondary">
                        {user.Category}
                      </Typography>
                    }
                    sx={{ pb: 0 }}
                  />

                  <CardContent sx={{ flexGrow: 1, pt: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {ReactHtmlParser(user.Summary || user.CaseStudyDescription?.substring(0, 120))}
                    </Typography>
                  </CardContent>

                  {/* Collapsible Details */}
                  <Collapse in={openCollapse[user.CaseStudyId]} timeout="auto" unmountOnExit>
                    <Divider />
                    <Box sx={{ p: 2, bgcolor: 'background.neutral' }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        <strong>Full Description:</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxHeight: 120,
                          overflow: 'auto',
                          fontSize: '0.85rem',
                        }}
                      >
                        {ReactHtmlParser(user.CaseStudyDescription)}
                      </Typography>
                      {user.Summary && (
                        <>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            <strong>Summary:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {user.Summary}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Collapse>
                </CaseStudyCard>
              </Fade>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // Render compact table view
  const renderTableView = () => {
    return (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e8ecf0' }}>
        <StickyTableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell align="center" sx={{ width: 40 }}>
                  #
                </StyledTableCell>
                <StyledTableCell>Case Study</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 80 }}>
                  Status
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 90 }}>
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((user, index) => {
                const imageUrl = user.CaseStudyFileName
                  ? `${axios.baseURL}CaseStudy/${user.CaseStudyFileName}`
                  : NoimageFound;
                return (
                  <StyledTableRow key={user.CaseStudyId} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 500, py: 1 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            p: 0.5,
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleImagePreview(imageUrl, user.CaseStudyTitle)}
                        >
                          <img
                            src={imageUrl}
                            alt={user.CaseStudyTitle}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                            {user.CaseStudyTitle}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {user.Category}
                            {user.CompanyName && (
                              <Chip
                                icon={<BusinessIcon sx={{ fontSize: 12 }} />}
                                label={user.CompanyName}
                                size="small"
                                sx={{ ml: 0.5, height: 18, fontSize: '0.55rem' }}
                              />
                            )}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Chip
                        icon={
                          user.ActiveStatus === 'Y' ? (
                            <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <HighlightOffIcon sx={{ fontSize: 14 }} />
                          )
                        }
                        label={user.ActiveStatus === 'Y' ? 'Active' : 'Inactive'}
                        color={user.ActiveStatus === 'Y' ? 'success' : 'error'}
                        size="small"
                        sx={{ fontWeight: 600, height: 22, fontSize: '0.65rem' }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(user)}
                          sx={{ color: 'primary.main', p: 0.5 }}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(user.CaseStudyId)}
                          sx={{ color: 'error.main', p: 0.5 }}
                        >
                          <DeleteForeverIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={openCollapse[user.CaseStudyId] ? 'Hide Details' : 'Show Details'}>
                        <IconButton
                          size="small"
                          onClick={() => toggleCollapse(user.CaseStudyId)}
                          sx={{ color: 'success.main', p: 0.5 }}
                        >
                          {openCollapse[user.CaseStudyId] ? (
                            <DoNotDisturbOnIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <ArrowDropDownCircleIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </StickyTableContainer>
      </Paper>
    );
  };

  return (
    <>
      <style>{`
        .case-editor-wrapper {
          position: relative;
        }
        .case-editor .ql-container {
          min-height: 200px;
          max-height: 60vh;
          overflow-y: auto;
        }
        .case-editor .ql-editor {
          white-space: pre-wrap;
          min-height: 150px;
        }
        .case-editor .ql-editor p {
          margin: 0 0 0.75rem;
        }
        .case-editor .ql-editor h1, 
        .case-editor .ql-editor h2, 
        .case-editor .ql-editor h3, 
        .case-editor .ql-editor h4 {
          margin-top: 0;
          margin-bottom: 0.5rem;
        }
        .case-editor .ql-editor h1:first-child,
        .case-editor .ql-editor h2:first-child,
        .case-editor .ql-editor h3:first-child,
        .case-editor .ql-editor h4:first-child {
          margin-top: 0;
        }
        .case-editor-wrapper.fullscreen-editor {
          position: fixed;
          inset: 20px;
          background: #fff;
          z-index: 1400;
          padding: 12px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.22);
          border-radius: 12px;
        }
        .case-editor-wrapper.fullscreen-editor .case-editor {
          height: calc(100vh - 120px);
        }
        .case-editor-wrapper.fullscreen-editor .ql-container {
          height: calc(100vh - 180px);
          max-height: none;
        }
        /* Sticky Dialog Styles */
        .sticky-dialog .MuiDialog-paper {
          display: flex;
          flex-direction: column;
          max-height: 95vh;
        }
        .sticky-dialog .MuiDialogContent-root {
          flex: 1 1 auto;
          overflow-y: auto;
          padding: 24px;
        }
        .sticky-dialog .MuiDialogActions-root {
          flex-shrink: 0;
        }
        .sticky-dialog .MuiDialogTitle-root {
          flex-shrink: 0;
        }
      `}</style>

      <Snackbar
        open={openAlert}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert onClose={handleCloseAlert} severity={alertType} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
          {alertMessage}
        </Alert>
      </Snackbar>

      <Container maxWidth="xl" sx={{ pt: 3, pb: 4 }}>
        {/* Header */}
        <HeaderPaper elevation={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <BusinessIcon sx={{ color: 'white' }} />
              <Typography variant="h6" fontWeight={700} color="white" sx={{ letterSpacing: '0.02em' }}>
                Manage Case Studies
              </Typography>
            </Stack>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Total: {CaseStudyData.length} case studies
            </Typography>
          </Stack>
        </HeaderPaper>

        {/* Search & Actions Bar */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 3 }}
        >
          <TextField
            placeholder="Search case studies..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { md: 200 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Badge badgeContent={getActiveFilterCount()} color="primary" invisible={getActiveFilterCount() === 0}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={handleFilterOpen}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                Filters
              </Button>
            </Badge>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
              sx={{ '& .MuiToggleButton-root': { px: 1.5 } }}
            >
              <ToggleButton value="grid">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="table">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<PlaylistAddIcon />}
              onClick={handleAddOpen}
              sx={{ borderRadius: 2, boxShadow: 2 }}
            >
              New Case Study
            </Button>
          </Stack>
        </Stack>

        {/* Filter Popover */}
        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              maxWidth: 320,
              width: '100%',
              p: 2,
            },
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Filter Case Studies
          </Typography>

          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              size="small"
              options={statusOptions}
              getOptionLabel={(option) => option.label}
              value={activeFilter}
              onChange={(e, newValue) => setActiveFilter(newValue)}
              renderInput={(params) => <TextField {...params} label="Status" size="small" />}
            />

            <Autocomplete
              size="small"
              options={reviewOptions}
              getOptionLabel={(option) => option.label}
              value={reviewFilter}
              onChange={(e, newValue) => setReviewFilter(newValue)}
              renderInput={(params) => <TextField {...params} label="Review" size="small" />}
            />

            <TextField
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              size="small"
              fullWidth
              placeholder="Filter by category..."
            />

            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" fullWidth onClick={clearAllFilters} startIcon={<ClearIcon />}>
                Clear All
              </Button>
              <Button variant="outlined" size="small" fullWidth onClick={handleFilterClose}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </Popover>

        {/* Results count */}
        {filteredData.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Showing {filteredData.length} case stud{filteredData.length !== 1 ? 'ies' : 'y'}
          </Typography>
        )}

        {/* Content */}
        {filteredData.length === 0 ? (
          <Paper sx={{ textAlign: 'center', py: 6, borderRadius: 3 }}>
            <Typography variant="h6" color="text.secondary">
              No case studies found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Try adjusting your filters or create a new case study
            </Typography>
          </Paper>
        ) : viewMode === 'grid' ? (
          renderCardView()
        ) : (
          renderTableView()
        )}
      </Container>

      {/* Fullscreen Image Preview */}
      <FullscreenImageDialog
        open={previewImageOpen}
        imageUrl={previewImageUrl}
        onClose={handleCloseImagePreview}
        title={previewImageTitle}
      />

      {/* Add/Edit Dialog - Redesigned with Sticky Header & Footer */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullScreen={isMobile}
        fullWidth
        className="sticky-dialog"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '95vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Sticky Header */}
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #e8ecf0',
              bgcolor: '#fafbfc',
              py: 2,
              pr: 2,
              flexWrap: 'wrap',
              gap: 1,
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {`${operation} Case Study`}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          {/* Scrollable Content */}
          <DialogContent
            sx={{
              p: 3,
              overflow: 'auto',
              flex: '1 1 auto',
              '&:first-of-type': { pt: 3 },
            }}
          >
            <Grid container spacing={2}>
              {/* Image Upload - Left Column */}
              <Grid item xs={12} md={3}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box
                    sx={{
                      border: `2px dashed ${errors.selectedImageFile ? '#d32f2f' : '#e0e0e0'}`,
                      borderRadius: 2,
                      p: 1,
                      width: '100%',
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <label htmlFor="fileInput" style={{ cursor: 'pointer' }}>
                      <img
                        src={selectedImageFile && imagePreview ? imagePreview : imagepreview}
                        alt="upload"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '120px',
                          objectFit: 'contain',
                        }}
                      />
                      <input
                        id="fileInput"
                        type="file"
                        accept="image/jpeg, image/png, image/jpg"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </Box>
                  {errors.selectedImageFile && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {helperTexts.selectedImageFile}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Form Fields - Right Column */}
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Title"
                      name="CaseStudyTitle"
                      value={CaseStudyTitle}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      fullWidth
                      size="small"
                      error={errors.CaseStudyTitle}
                      helperText={helperTexts.CaseStudyTitle}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={reviewData}
                      getOptionLabel={(option) => option.CompanyName || ''}
                      value={Review || null}
                      onChange={(event, newValue) => handleReviewChange(newValue)}
                      isOptionEqualToValue={(option, value) => option.ReviewId === value?.ReviewId}
                      name="Review"
                      clearOnEscape
                      onBlur={() => handleBlur({ target: { name: 'Review', value: Review } })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Associated Review"
                          variant="outlined"
                          color="primary"
                          name="Review"
                          fullWidth
                          size="small"
                          error={Boolean(errors.Review)}
                          helperText={helperTexts.Review}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Category"
                      name="Category"
                      value={Category}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      fullWidth
                      size="small"
                      error={errors.Category}
                      helperText={helperTexts.Category}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Blog Content - Full Width */}
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Description *
                  </Typography>
                  <IconButton onClick={toggleEditorFullscreen} size="small">
                    {isEditorFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                </Stack>
                <div ref={editorWrapperRef} className="case-editor-wrapper">
                  {isEditorFullscreen && (
                    <IconButton
                      onClick={toggleEditorFullscreen}
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 10,
                        bgcolor: 'rgba(17,24,39,0.75)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(17,24,39,0.9)' },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                  <ReactQuill
                    value={CaseStudyDescription}
                    onChange={handleChange2}
                    theme="snow"
                    modules={quillModules}
                    className="case-editor"
                  />
                </div>
                {errors.CaseStudyDescription && (
                  <Typography variant="caption" color="error">
                    {helperTexts.CaseStudyDescription}
                  </Typography>
                )}
              </Grid>

              {/* Summary */}
              <Grid item xs={12}>
                <TextField
                  label="Summary"
                  name="Summary"
                  multiline
                  rows={3}
                  value={Summary}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  fullWidth
                  size="small"
                  error={errors.Summary}
                  helperText={helperTexts.Summary}
                />
              </Grid>
            </Grid>
          </DialogContent>

          {/* Sticky Footer with Switch and Buttons */}
          <DialogActions
            sx={{
              p: 2,
              borderTop: '1px solid #e8ecf0',
              bgcolor: '#fafbfc',
              gap: 2,
              flexShrink: 0,
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {/* Left side - Status Switch */}
            <Stack direction="row" spacing={2} alignItems="center">
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ActiveStatus === 'Y'}
                      onChange={(e) =>
                        handleInputChange({ target: { name: 'ActiveStatus', value: e.target.checked ? 'Y' : 'N' } })
                      }
                      color="success"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      {ActiveStatus === 'Y' ? 'Active' : 'Inactive'}
                    </Typography>
                  }
                />
              </FormGroup>
            </Stack>

            {/* Right side - Action Buttons */}
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={handleClose} size="medium">
                Cancel
              </Button>
              <Button type="submit" variant="contained" size="medium">
                {operation === 'Add' ? 'Create Case Study' : 'Update Case Study'}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDelete} onClose={handleCloseDelete} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Case Study?</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={handleCloseDelete}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loader */}
      <Dialog open={openLoader} fullWidth>
        <LinearProgress />
      </Dialog>
    </>
  );
};

export default CaseStudy;
